// theme/src/gift-wrap/src/packaging/buildChoices.js
import { PRESENTATION } from './selection';

const NAME_VARIANT_WORDS = ['custom text', 'add a name'];
const NO_STAMP = { vId_stamp: null, vId_stamp_label: null };

/** Same rule as BagOrBox/useViewModal.js: the variant that carries the name price. */
export const isNameVariant = (variant) =>
  NAME_VARIANT_WORDS.some((word) => (variant?.title || '').toLowerCase().includes(word));

export const plainVariant = (option) => option?.variants?.find((v) => !isNameVariant(v)) ?? option?.variants?.[0] ?? null;

export const nameVariant = (option) => option?.variants?.find(isNameVariant) ?? null;

const stampFor = (stamp, chosenId) => {
  if (!stamp) return NO_STAMP;
  const variant = stamp.variants?.find((v) => v.id === chosenId) ?? stamp.variants?.[0];
  return variant ? { vId_stamp: variant.id, vId_stamp_label: variant.title } : NO_STAMP;
};

const toChoice = ({ option, variant, text = null, stamp = NO_STAMP }) => ({
  type: option.type,
  parentId: option.product_id,
  vId_gift: variant.id,
  text,
  ...stamp,
  title: option.title,
  price: String(variant.price ?? 0),
  vendor: option.vendor || null
});

/**
 * Turns the packaging selection into today's personalisationChoices, so the existing cart writer
 * (buildGiftWrapItems) writes exactly today's line item properties (appendix 9.4).
 * @param {{ selection: import('./selection').initialSelection, groups: ReturnType<import('./options').groupPackagingOptions> }} args
 * @returns {Array<Object>}
 */
export function buildPackagingChoices({ selection, groups }) {
  const choices = [];
  const boxOption = selection.box ? groups[selection.box] : null;
  if (boxOption && plainVariant(boxOption)) {
    choices.push(toChoice({ option: boxOption, variant: plainVariant(boxOption) }));
  }

  const { presentation, letters, sealsOn } = selection;
  // Only a seal the customer turned on gets a stamp; with every seal off the lines match the old modal's
  // "no wax stamp" output.
  const seal = (key, stamp) => (sealsOn?.[key] ? stampFor(stamp, letters[key]) : NO_STAMP);
  if (presentation === PRESENTATION.BAG && groups.bag) {
    const name = selection.bagNameOn ? selection.bagName.trim() : '';
    const named = name ? nameVariant(groups.bag) : null;
    choices.push(
      toChoice({
        option: groups.bag,
        variant: named ?? plainVariant(groups.bag),
        text: named ? name : null,
        stamp: seal('bag', groups.stampBag)
      })
    );
  }

  if (presentation === PRESENTATION.PAIR && groups.bag && groups.box) {
    choices.push(
      toChoice({ option: groups.bag, variant: plainVariant(groups.bag), stamp: seal('bag', groups.stampBag) })
    );
    choices.push(
      toChoice({ option: groups.box, variant: plainVariant(groups.box), stamp: seal('box', groups.stampBox) })
    );
  }

  if (presentation === PRESENTATION.KEEPSAKE && groups.keepsake) {
    choices.push(
      toChoice({
        option: groups.keepsake,
        variant: plainVariant(groups.keepsake),
        text: selection.lidName.trim() || null,
        stamp: seal('box', groups.stampBox)
      })
    );
  }

  return choices;
}
